import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getMe } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext();

// Helper to decode JWT and check expiry
const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const isTokenExpiringSoon = (token) => {
  const decoded = parseJwt(token);
  if (!decoded?.exp) return true;
  // Refresh if less than 1 day until expiry
  const expiryTime = decoded.exp * 1000;
  const oneDayMs = 24 * 60 * 60 * 1000;
  return Date.now() > expiryTime - oneDayMs;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const refreshToken = useCallback(async () => {
    try {
      const res = await api.post('/auth/refresh');
      const newToken = res.data.token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    if (token) {
      // Check if token is expiring soon and refresh it
      if (isTokenExpiringSoon(token)) {
        refreshToken();
      }
      
      getMe()
        .then((res) => {
          setUser(res.data);
        })
        .catch(() => {
          localStorage.removeItem('token');
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, refreshToken]);

  // Set up periodic token refresh check (every 6 hours)
  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      if (token && isTokenExpiringSoon(token)) {
        refreshToken();
      }
    }, 6 * 60 * 60 * 1000); // 6 hours
    
    return () => clearInterval(interval);
  }, [token, refreshToken]);

  const loginUser = (token, userData) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logoutUser, isAuthenticated: !!user, refreshToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
